import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { adminAPI } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Dumbbell, UserCheck, UserX, Clock, Users,
  CheckCircle, XCircle, TrendingUp, Mail,
  Calendar, Scale, Target, Activity, MessageSquare, Crown,
  Eye, Archive, UserCircle, AlertTriangle, Ban, Star, BarChart3, IndianRupee
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { getImageUrl, getInitials } from "../lib/avatarUtils";
import { Avatar } from "../components/Avatar";

export default function AdminPage() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [analytics, setAnalytics] = useState({ total_trainers: 0, total_clients: 0, active_trainers: 0, pending_requests: 0 });
  const [trainers, setTrainers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Trainer clients modal
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerClients, setTrainerClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  
  // Feedback state
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  
  // Coach Profile state
  const [coachProfile, setCoachProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [loadingUserAnalytics, setLoadingUserAnalytics] = useState(false);

  useEffect(() => {
    if (!trainer?.is_admin) {
      navigate("/dashboard");
      return;
    }
    loadAllData();
  }, [trainer, navigate]);

  const loadAllData = async () => {
    try {
      const [analyticsRes, trainersRes, pendingRes, unreadRes] = await Promise.all([
        adminAPI.getAnalytics(),
        adminAPI.getTrainers(),
        adminAPI.getPendingUsers(),
        adminAPI.getUnreadFeedbackCount()
      ]);
      setAnalytics(analyticsRes.data);
      setTrainers(trainersRes.data);
      setPendingUsers(pendingRes.data);
      setUnreadFeedbackCount(unreadRes.data.unread_count || 0);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    setProcessing(userId);
    try {
      await adminAPI.approveUser(userId);
      toast.success(`${userName} has been approved`);
      loadAllData();
    } catch (error) {
      toast.error("Failed to approve user");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId, userName) => {
    setProcessing(userId);
    try {
      await adminAPI.rejectUser(userId);
      toast.success(`${userName} has been rejected`);
      loadAllData();
    } catch (error) {
      toast.error("Failed to reject user");
    } finally {
      setProcessing(null);
    }
  };

  const handleViewTrainerClients = async (trainerData) => {
    setSelectedTrainer(trainerData);
    setLoadingClients(true);
    try {
      const response = await adminAPI.getTrainerClients(trainerData.id);
      setTrainerClients(response.data.clients);
    } catch (error) {
      toast.error("Failed to load trainer clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const handleViewCoachProfile = async (trainerData) => {
    setLoadingProfile(true);
    setCoachProfile({ ...trainerData, loading: true });
    try {
      const response = await adminAPI.getTrainerProfile(trainerData.id);
      setCoachProfile(response.data);
    } catch (error) {
      toast.error("Failed to load coach profile");
      setCoachProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const getPlanBadgeStyle = (planType) => {
    switch (planType) {
      case "pro":
        return "bg-primary/10 text-primary border-primary/30";
      case "beta":
        return "bg-amber-500/10 text-amber-400 border-amber-500/30";
      default:
        return "bg-secondary text-muted-foreground";
    }
  };

  const loadFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const response = await adminAPI.getFeedback();
      setFeedback(response.data);
      // Update unread count when we load feedback
      const unreadRes = await adminAPI.getUnreadFeedbackCount();
      setUnreadFeedbackCount(unreadRes.data.unread_count || 0);
    } catch (error) {
      toast.error("Failed to load feedback");
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, status) => {
    try {
      await adminAPI.updateFeedbackStatus(feedbackId, status);
      toast.success(`Feedback marked as ${status}`);
      loadFeedback();
      // Update unread count after status change
      const unreadRes = await adminAPI.getUnreadFeedbackCount();
      setUnreadFeedbackCount(unreadRes.data.unread_count || 0);
    } catch (error) {
      toast.error("Failed to update feedback status");
    }
  };

  // Coach management functions
  const handleSuspendCoach = async (trainerId, name) => {
    try {
      await adminAPI.suspendTrainer(trainerId);
      toast.success(`${name} has been suspended`);
      loadAllData();
      if (coachProfile?.id === trainerId) {
        handleViewCoachProfile({ id: trainerId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suspend coach");
    }
  };

  const handleUnsuspendCoach = async (trainerId, name) => {
    try {
      await adminAPI.reactivateTrainer(trainerId);
      toast.success(`${name} has been reactivated`);
      loadAllData();
      if (coachProfile?.id === trainerId) {
        handleViewCoachProfile({ id: trainerId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate coach");
    }
  };

  const handleMakeCoachPro = async (trainerId, name) => {
    try {
      await adminAPI.makeTrainerPro(trainerId);
      toast.success(`${name} has been upgraded to Pro`);
      loadAllData();
      if (coachProfile?.id === trainerId) {
        handleViewCoachProfile({ id: trainerId });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upgrade coach");
    }
  };

  const handleSendWarning = async (trainerId, name) => {
    try {
      await adminAPI.sendWarning(trainerId, "Please review your account activity and ensure compliance with our terms.");
      toast.success(`Warning sent to ${name}`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send warning");
    }
  };

  useEffect(() => {
    if (activeTab === "feedback") {
      loadFeedback();
    }
  }, [activeTab]);

  const getGoalBadgeClass = (goal) => {
    switch (goal) {
      case "fat_loss": return "bg-red-500/10 text-red-400 border-red-500/30";
      case "muscle_gain": return "bg-green-500/10 text-green-400 border-green-500/30";
      default: return "bg-blue-500/10 text-blue-400 border-blue-500/30";
    }
  };

  const getGoalLabel = (goal) => {
    switch (goal) {
      case "fat_loss": return "Fat Loss";
      case "muscle_gain": return "Muscle Gain";
      default: return "Maintenance";
    }
  };

  const pendingOnly = pendingUsers.filter(u => u.status === "pending");
  const rejectedOnly = pendingUsers.filter(u => u.status === "rejected");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8">
      {/* Header */}
      <Header title="Admin Panel" subtitle="View coaches and monitor platform activity" />

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* View Only Banner */}
        <Card className="bg-secondary/20 border-0">
          <CardContent className="p-4 flex items-center gap-3">
            <Eye className="w-5 h-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">View Only Mode:</span> Admin can view coach profiles and monitor activity but cannot add, edit, or delete client data.
            </p>
          </CardContent>
        </Card>

        {/* Analytics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card className="glass-card border-0" data-testid="total-coaches-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-muted-foreground truncate">
                    Coaches
                  </p>
                  <p className="font-heading text-xl sm:text-3xl font-bold text-foreground">
                    {analytics.total_trainers}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0" data-testid="total-clients-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-neon-green/10 flex items-center justify-center shrink-0">
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-neon-green" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-muted-foreground truncate">
                    Clients
                  </p>
                  <p className="font-heading text-xl sm:text-3xl font-bold text-neon-green">
                    {analytics.total_clients}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0" data-testid="active-coaches-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-yellow-500/10 flex items-center justify-center shrink-0">
                  <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-muted-foreground truncate">
                    Active
                  </p>
                  <p className="font-heading text-xl sm:text-3xl font-bold text-yellow-500">
                    {analytics.active_trainers}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0" data-testid="deactivated-coaches-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-muted-foreground truncate">
                    Deactivated
                  </p>
                  <p className="font-heading text-xl sm:text-3xl font-bold text-orange-500">
                    {analytics.deactivated_trainers || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0 col-span-2 sm:col-span-1" data-testid="pending-requests-card">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-muted-foreground truncate">
                    Pending
                  </p>
                  <p className="font-heading text-xl sm:text-3xl font-bold text-orange-500">
                    {analytics.pending_requests}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          if (v === "analytics" && !userAnalytics) {
            setLoadingUserAnalytics(true);
            adminAPI.getUserAnalytics().then(res => setUserAnalytics(res.data)).catch(() => toast.error("Failed to load analytics")).finally(() => setLoadingUserAnalytics(false));
          }
        }} className="space-y-4">
          <TabsList className="grid grid-cols-4 bg-white/[0.03] p-1 sm:p-1.5 h-auto rounded-xl w-full max-w-xl">
            <TabsTrigger
              value="dashboard"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-2.5 sm:py-3 font-heading uppercase tracking-wide text-[10px] sm:text-xs rounded-lg transition-all"
              data-testid="coaches-tab"
            >
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
              Coaches
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-2.5 sm:py-3 font-heading uppercase tracking-wide text-[10px] sm:text-xs rounded-lg transition-all"
              data-testid="analytics-tab"
            >
              <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
              Analytics
            </TabsTrigger>
            <TabsTrigger
              value="requests"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-2.5 sm:py-3 font-heading uppercase tracking-wide text-[10px] sm:text-xs rounded-lg transition-all"
              data-testid="requests-tab"
            >
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
              Requests {pendingOnly.length > 0 && `(${pendingOnly.length})`}
            </TabsTrigger>
            <TabsTrigger
              value="feedback"
              className="data-[state=active]:tab-active data-[state=active]:text-white py-2.5 sm:py-3 font-heading uppercase tracking-wide text-[10px] sm:text-xs rounded-lg transition-all relative"
              data-testid="feedback-tab"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Feedback
              {unreadFeedbackCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-white text-xs font-bold flex items-center justify-center px-1" data-testid="feedback-badge">
                  {unreadFeedbackCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4 animate-in">
            {loadingUserAnalytics ? (
              <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
            ) : userAnalytics ? (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Users</p>
                      <p className="text-2xl font-extrabold text-foreground mt-1">{userAnalytics.total_users}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Active (7d)</p>
                      <p className="text-2xl font-extrabold text-green-400 mt-1">{userAnalytics.active_7d}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Paid Users</p>
                      <p className="text-2xl font-extrabold text-primary mt-1">{userAnalytics.paid_users}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Revenue</p>
                      <p className="text-2xl font-extrabold text-amber-400 mt-1">₹{(userAnalytics.total_revenue || 0).toLocaleString()}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Conversion & Active Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Conversion Rate</p>
                      <p className="text-xl font-extrabold text-primary mt-1">{userAnalytics.conversion_rate}%</p>
                      <p className="text-[10px] text-muted-foreground">Free → Paid</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Active (30d)</p>
                      <p className="text-xl font-extrabold text-foreground mt-1">{userAnalytics.active_30d}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Referrals</p>
                      <p className="text-xl font-extrabold text-foreground mt-1">{userAnalytics.total_referrals}</p>
                    </CardContent>
                  </Card>
                  <Card className="glass-card border-0">
                    <CardContent className="p-4 text-center">
                      <p className="text-[10px] text-muted-foreground uppercase font-bold">Payments</p>
                      <p className="text-xl font-extrabold text-foreground mt-1">{userAnalytics.total_payments}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Plan Breakdown */}
                <Card className="glass-card border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base tracking-wide uppercase">Plan Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-2">
                      {Object.entries(userAnalytics.plan_breakdown || {}).map(([plan, count]) => {
                        const colors = { beta: "text-muted-foreground", free: "text-muted-foreground", pro: "text-primary", elite: "text-amber-400" };
                        const bgs = { beta: "bg-white/[0.03]", free: "bg-white/[0.03]", pro: "bg-primary/10", elite: "bg-amber-500/10" };
                        return (
                          <div key={plan} className={`p-3 rounded-xl text-center ${bgs[plan] || "bg-white/[0.03]"}`}>
                            <p className={`text-lg font-extrabold ${colors[plan] || ""}`}>{count}</p>
                            <p className="text-[10px] text-muted-foreground uppercase font-bold capitalize">{plan === "beta" ? "Free" : plan}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Daily Signups Chart */}
                <Card className="glass-card border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base tracking-wide uppercase">Daily Signups (30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={userAnalytics.signup_chart || []}>
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#71717a' }} tickFormatter={(d) => d.slice(5)} />
                        <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                          labelFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        />
                        <Bar dataKey="count" fill="#00BFFF" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Revenue Chart */}
                {(userAnalytics.revenue_chart || []).length > 0 && (
                  <Card className="glass-card border-0">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-base tracking-wide uppercase">Monthly Revenue</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={userAnalytics.revenue_chart}>
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#71717a' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#71717a' }} tickFormatter={(v) => `₹${v}`} />
                          <Tooltip
                            contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                            formatter={(v) => [`₹${v.toLocaleString()}`, 'Revenue']}
                          />
                          <Bar dataKey="amount" fill="#22C55E" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Signups */}
                <Card className="glass-card border-0">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base tracking-wide uppercase">Recent Signups</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(userAnalytics.recent_signups || []).map((u, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {u.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{u.name}</p>
                              <p className="text-xs text-muted-foreground">{u.email || u.phone || "No contact"}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={`text-[10px] ${
                              u.plan === "pro" ? "bg-primary/20 text-primary border-primary/30" :
                              u.plan === "elite" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                              "bg-white/[0.05] text-muted-foreground border-white/[0.06]"
                            }`}>{u.plan === "beta" ? "Free" : u.plan}</Badge>
                            <span className="text-[10px] text-muted-foreground">
                              {u.clients} clients · {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ""}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </TabsContent>

          {/* Coaches Tab */}
          <TabsContent value="dashboard" className="space-y-4 animate-in">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg tracking-wide uppercase text-muted-foreground">
                Coaches / Trainers
              </h2>
              <p className="text-xs text-muted-foreground">
                {trainers.filter(t => !t.is_admin).length} registered coaches
              </p>
            </div>
            
            {trainers.filter(t => !t.is_admin).length === 0 ? (
              <Card className="empty-state rounded-2xl">
                <CardContent className="p-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 empty-state-icon" />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                    No Coaches Yet
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                    Coaches will appear here once they sign up and get approved.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {trainers.filter(t => !t.is_admin).map((trainerItem) => (
                  <Card 
                    key={trainerItem.id} 
                    className="glass-card border-0"
                    data-testid={`trainer-${trainerItem.id}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {/* Coach Avatar */}
                          <Avatar src={trainerItem.profile_photo} name={trainerItem.name} size="md" testId={`coach-avatar-${trainerItem.id}`} />
                          <div className="flex-1 min-w-0">
                          {/* Name and Badges Row */}
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <h3 className="font-heading text-lg font-bold text-foreground">
                              {trainerItem.name}
                            </h3>
                            {/* Plan Type Badge - Always show Beta during beta period */}
                            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                              Beta
                            </Badge>
                            {/* Status Badge */}
                            {trainerItem.status === "suspended" ? (
                              <Badge className="bg-destructive/10 text-destructive border-destructive/30 text-xs flex items-center gap-1">
                                <Ban className="w-3 h-3" />
                                Suspended
                              </Badge>
                            ) : trainerItem.status === "deactivated" ? (
                              <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/30 text-xs flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                Deactivated
                              </Badge>
                            ) : trainerItem.status === "pending" ? (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                                Pending
                              </Badge>
                            ) : (
                              <Badge className="bg-neon-green/10 text-neon-green border-neon-green/30 text-xs">
                                Active
                              </Badge>
                            )}
                          </div>
                          
                          {/* Email Row */}
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                            <Mail className="w-3.5 h-3.5" />
                            {trainerItem.email}
                          </div>
                          
                          {/* Stats Row */}
                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="w-3.5 h-3.5" />
                              Joined {new Date(trainerItem.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            <span className="flex items-center gap-1 text-primary">
                              <Users className="w-3.5 h-3.5" />
                              {trainerItem.active_client_count || trainerItem.client_count || 0} clients
                            </span>
                            {trainerItem.archived_client_count > 0 && (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <Archive className="w-3.5 h-3.5" />
                                {trainerItem.archived_client_count} archived
                              </span>
                            )}
                          </div>
                          
                          {/* Last Active */}
                          {trainerItem.last_active && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Activity className="w-3 h-3" />
                              Last active: {new Date(trainerItem.last_active).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          
                          {/* Invited By */}
                          {trainerItem.invited_by_name && (
                            <p className="text-xs text-primary mt-1 flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Invited by {trainerItem.invited_by_name}
                            </p>
                          )}
                        </div>
                        </div>
                        
                        {/* Action Button */}
                        <Button
                          onClick={() => handleViewCoachProfile(trainerItem)}
                          variant="outline"
                          size="sm"
                          className="rounded-lg shrink-0 w-full sm:w-auto"
                          data-testid={`view-coach-${trainerItem.id}`}
                        >
                          <UserCircle className="w-4 h-4 mr-2" />
                          View Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-6 animate-in">
            {/* Pending */}
            <div className="space-y-3">
              <h2 className="font-heading text-lg tracking-wide uppercase text-muted-foreground">
                Pending Requests
              </h2>
              
              {pendingOnly.length === 0 ? (
                <Card className="empty-state rounded-2xl">
                  <CardContent className="p-10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 empty-state-icon" />
                    </div>
                    <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                      All Caught Up
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                      No pending access requests at the moment.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                pendingOnly.map((user) => (
                  <Card key={user.id} className="glass-card border-0" data-testid={`pending-${user.id}`}>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="font-heading text-lg font-bold text-foreground">
                              {user.name}
                            </h3>
                            <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 text-xs">
                              Pending
                            </Badge>
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/30 text-xs">
                              Email Verified
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Requested {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {user.invited_by_name && (
                              <span className="ml-2 text-primary">
                                &middot; Invited by {user.invited_by_name}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleApprove(user.id, user.name); }}
                            disabled={processing === user.id}
                            className="btn-premium text-white rounded-lg h-10 px-4"
                            data-testid={`approve-${user.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleReject(user.id, user.name); }}
                            disabled={processing === user.id}
                            className="border-destructive/50 text-destructive hover:bg-destructive/10 rounded-lg h-10 px-4"
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Rejected */}
            {rejectedOnly.length > 0 && (
              <div className="space-y-3">
                <h2 className="font-heading text-lg tracking-wide uppercase text-muted-foreground">
                  Rejected
                </h2>
                {rejectedOnly.map((user) => (
                  <Card key={user.id} className="glass-card border-0 opacity-60" data-testid={`rejected-${user.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-heading text-lg font-bold text-foreground">
                              {user.name}
                            </h3>
                            <Badge className="bg-destructive/10 text-destructive border-destructive/30 text-xs">
                              Rejected
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleApprove(user.id, user.name)}
                          disabled={processing === user.id}
                          className="border-primary/50 text-primary hover:bg-primary/10 rounded-lg h-10 px-4"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-4 animate-in">
            <h2 className="font-heading text-lg tracking-wide uppercase text-muted-foreground">
              User Feedback
            </h2>
            
            {loadingFeedback ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : feedback.length === 0 ? (
              <Card className="empty-state rounded-2xl">
                <CardContent className="p-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 empty-state-icon" />
                  </div>
                  <h3 className="font-heading text-lg font-bold tracking-tight uppercase text-foreground mb-2">
                    No Feedback Yet
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                    User feedback will appear here once submitted.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {feedback.map((item) => (
                  <Card key={item.id} className="glass-card border-0" data-testid={`feedback-${item.id}`}>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-heading text-base font-bold text-foreground">
                              {item.trainer_name}
                            </h3>
                            <Badge className={`text-xs ${
                              item.category === 'bug' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                              item.category === 'feature' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                              item.category === 'other' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' :
                              'bg-secondary text-muted-foreground'
                            }`}>
                              {item.category === 'general' ? 'General Feedback' :
                               item.category === 'feature' ? 'Feature Request' :
                               item.category === 'bug' ? 'Bug Report' :
                               item.category === 'other' ? 'Other' : item.category}
                            </Badge>
                            <Badge className={`text-xs ${
                              item.status === 'new' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                              item.status === 'reviewed' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                              'bg-neon-green/10 text-neon-green border-neon-green/30'
                            }`}>
                              {item.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{item.trainer_email}</p>
                          <p className="text-foreground">{item.message}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(item.created_at).toLocaleDateString('en-US', { 
                              month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })}
                          </p>
                        </div>
                        <div className="flex flex-row sm:flex-col gap-2">
                          {item.status === 'new' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateFeedbackStatus(item.id, 'reviewed')}
                              className="text-xs"
                              data-testid={`mark-reviewed-${item.id}`}
                            >
                              Mark Reviewed
                            </Button>
                          )}
                          {item.status !== 'resolved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateFeedbackStatus(item.id, 'resolved')}
                              className="text-xs text-neon-green border-neon-green/30 hover:bg-neon-green/10"
                              data-testid={`mark-resolved-${item.id}`}
                            >
                              Mark Resolved
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Coach Clients Modal - READ ONLY */}
      <Dialog open={!!selectedTrainer} onOpenChange={() => setSelectedTrainer(null)}>
        <DialogContent className="bg-card border-border max-w-2xl sm:max-h-[85vh] flex flex-col p-0 overflow-hidden gap-0">
          <div className="shrink-0 p-4 sm:p-6 pb-3 border-b border-border/50">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase">
              {selectedTrainer?.name}&apos;s Clients
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{selectedTrainer?.email} &middot; View Only</p>
          </DialogHeader>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 min-h-0">
            {loadingClients ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : trainerClients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No clients yet</p>
              </div>
            ) : (
              trainerClients.map((client) => (
                <Card key={client.id} className="bg-white/[0.05] border border-white/[0.08] rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-heading text-base font-bold text-foreground">
                            {client.name}
                          </h4>
                          <Badge className={`${getGoalBadgeClass(client.goal)} text-xs`}>
                            {getGoalLabel(client.goal)}
                          </Badge>
                          {!client.is_active && (
                            <Badge className="bg-muted text-muted-foreground text-xs">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {client.age} yrs, {client.gender}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1 text-sm">
                          <Scale className="w-4 h-4 text-primary" />
                          <span className="font-bold text-foreground">{client.current_weight || client.starting_weight}</span>
                          <span className="text-muted-foreground">kg</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Start: {client.starting_weight} kg
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Coach Profile Modal - READ ONLY */}
      <Dialog open={!!coachProfile} onOpenChange={() => setCoachProfile(null)}>
        <DialogContent className="bg-card border-border max-w-2xl sm:max-h-[85vh] flex flex-col p-0 overflow-hidden gap-0">
          <div className="shrink-0 p-4 sm:p-6 pb-2 border-b border-border/50">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase flex items-center gap-2">
              <UserCircle className="w-6 h-6 text-primary" />
              Coach Profile
            </DialogTitle>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              View Only - Admin cannot modify coach data
            </p>
          </DialogHeader>
          </div>
          
          {loadingProfile ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : coachProfile ? (
            <>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-6 min-h-0">
              {/* Coach Info */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 bg-[#00BFFF]/10 flex items-center justify-center" data-testid="coach-profile-modal-avatar">
                      <Avatar src={coachProfile.profile_photo} name={coachProfile.name} size="lg" testId="coach-profile-modal-avatar-inner" />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-foreground">
                        {coachProfile.name}
                      </h3>
                      <p className="text-muted-foreground flex items-center gap-1 mt-1">
                        <Mail className="w-4 h-4" />
                        {coachProfile.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {/* Always show Beta badge during beta period */}
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-sm">
                      Beta
                    </Badge>
                    {coachProfile.status === "suspended" ? (
                      <Badge className="bg-destructive/10 text-destructive border-destructive/30 text-sm flex items-center gap-1">
                        <Ban className="w-3.5 h-3.5" />
                        Suspended
                      </Badge>
                    ) : coachProfile.status === "pending" ? (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-sm">
                        Pending
                      </Badge>
                    ) : (
                      <Badge className="bg-neon-green/10 text-neon-green border-neon-green/30 text-sm">
                        Active
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Signup Date & Beta End */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/[0.03] rounded-xl">
                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-1">
                      Signup Date
                    </p>
                    <p className="text-foreground font-medium">
                      {new Date(coachProfile.created_at).toLocaleDateString('en-US', { 
                        month: 'long', day: 'numeric', year: 'numeric' 
                      })}
                    </p>
                  </div>
                  {coachProfile.plan_type === "beta" && coachProfile.beta_end_date && (
                    <div className="p-4 bg-amber-500/10 rounded-xl">
                      <p className="text-xs font-bold tracking-widest uppercase text-amber-400 mb-1">
                        Beta Ends
                      </p>
                      <p className="text-amber-400 font-medium">
                        {new Date(coachProfile.beta_end_date).toLocaleDateString('en-US', { 
                          month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Section */}
              <div className="border-t border-border pt-6">
                <h4 className="font-heading text-sm font-bold tracking-widest uppercase text-muted-foreground mb-4">
                  Client Statistics
                </h4>
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 bg-primary/10 rounded-xl text-center">
                    <p className="font-heading text-2xl sm:text-3xl font-bold text-primary">
                      {coachProfile.stats?.active_clients || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Active Clients</p>
                  </div>
                  <div className="p-4 bg-white/[0.03] rounded-xl text-center">
                    <p className="font-heading text-3xl font-bold text-muted-foreground">
                      {coachProfile.stats?.archived_clients || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Archived</p>
                  </div>
                  <div className="p-4 bg-white/[0.03] rounded-xl text-center">
                    <p className="font-heading text-3xl font-bold text-foreground">
                      {coachProfile.stats?.total_clients || 0}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Total Clients</p>
                  </div>
                </div>
                
                {/* Activity Stats */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-white/[0.03] rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <Scale className="w-4 h-4 text-neon-green" />
                      <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Progress Entries
                      </p>
                    </div>
                    <p className="font-heading text-2xl font-bold text-foreground">
                      {coachProfile.stats?.progress_entries || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-white/[0.03] rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <Dumbbell className="w-4 h-4 text-primary" />
                      <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                        Workout Entries
                      </p>
                    </div>
                    <p className="font-heading text-2xl font-bold text-foreground">
                      {coachProfile.stats?.workout_entries || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              {coachProfile.recent_activity && coachProfile.recent_activity.length > 0 && (
                <div className="border-t border-border pt-6">
                  <h4 className="font-heading text-sm font-bold tracking-widest uppercase text-muted-foreground mb-4">
                    Recent Activity
                  </h4>
                  <div className="space-y-2">
                    {coachProfile.recent_activity.map((activity) => (
                      <div 
                        key={activity.id} 
                        className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${activity.is_archived ? 'bg-muted-foreground' : 'bg-neon-green'}`} />
                          <span className="text-foreground font-medium">{activity.name}</span>
                          {activity.is_archived && (
                            <Badge className="bg-muted text-muted-foreground text-xs">Archived</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(activity.updated_at).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Clients Button */}
              <div className="border-t border-border pt-6">
                <Button
                  onClick={() => {
                    const profileData = { ...coachProfile };
                    setCoachProfile(null);
                    setTimeout(() => handleViewTrainerClients(profileData), 200);
                  }}
                  variant="outline"
                  className="w-full rounded-lg"
                  data-testid="view-clients-from-profile"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View All Clients ({coachProfile.stats?.total_clients || 0})
                </Button>
              </div>
            </div>

            {/* Sticky Admin Actions Footer */}
            <div className="shrink-0 border-t border-border bg-card/95 backdrop-blur-sm p-4 sm:px-6" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
              <p className="font-heading text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">
                Admin Actions
              </p>
              <div className="grid grid-cols-2 gap-3">
                  {coachProfile.status === "approved" ? (
                    <Button
                      onClick={() => handleSuspendCoach(coachProfile.id, coachProfile.name)}
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/10"
                      data-testid="suspend-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Suspend
                    </Button>
                  ) : coachProfile.status === "suspended" || coachProfile.status === "deactivated" ? (
                    <Button
                      onClick={() => handleUnsuspendCoach(coachProfile.id, coachProfile.name)}
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-neon-green border-neon-green/30 hover:bg-neon-green/10"
                      data-testid="reactivate-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Activate
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => handleSendWarning(coachProfile.id, coachProfile.name)}
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                    data-testid="send-warning-btn"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Send Warning
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
